import { FC, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import upperFirst from 'lodash/upperFirst'
import { match } from 'ts-pattern'

import {
  Button,
  IconArrowRotateRightLeft,
  useNotifications
} from '@sequencehq/core-components'
import { useMutation } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'

import { apiQueryClient } from 'features/api/apiQueryClient'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { automationsPaths } from 'Automations/router'
import { getSyncErrorDescription } from 'Integrations/integrationsConfig/common/shared'
import { ProtectedButton } from 'lib/rbac'
import { serviceLabel } from './integrationSyncErrorMessages'
import { SyncErrorRepush } from './syncErrorRepush'

export const RepushSyncErrorAction: FC<{
  repush: SyncErrorRepush
  'data-testid'?: string
}> = ({ repush, 'data-testid': dataTestId }) => {
  const { repushToIntegration, isRepushing } = useRepushToIntegration(repush)

  return (
    <ProtectedButton permission={repush.permission}>
      <Button
        variant="secondary"
        submitting={isRepushing}
        submittingText={`Syncing ${repush.entityName}`}
        leadingIcon={<IconArrowRotateRightLeft />}
        onClick={repushToIntegration}
        data-testid={dataTestId}
      >
        Sync {repush.entityName}
      </Button>
    </ProtectedButton>
  )
}

const useRepushToIntegration = (repush: SyncErrorRepush) => {
  const { displayNotification } = useNotifications()
  const navigate = useNavigate()
  const pushPaymentMutation = useMutation(
    dashboard20240730Client.postPushPaymentToExternalService
  )
  const pushCreditNoteMutation = useMutation(
    dashboard20240730Client.postPushCreditNoteToExternalService
  )
  const pushCreditNoteApplicationMutation = useMutation(
    dashboard20240730Client.postPushCreditNoteApplicationToExternalService
  )

  const repushToIntegration = useCallback(async () => {
    const serviceName = serviceLabel(repush.service)

    try {
      await match(repush)
        .with({ entityType: 'PAYMENT' }, ({ service, paymentId }) =>
          pushPaymentMutation.mutateAsync({ service, paymentId })
        )
        .with({ entityType: 'CREDIT_NOTE' }, ({ service, creditNoteId }) =>
          pushCreditNoteMutation.mutateAsync({ service, creditNoteId })
        )
        .with(
          { entityType: 'CREDIT_NOTE_APPLICATION' },
          ({ service, creditNoteId, ledgerTransactionId }) =>
            pushCreditNoteApplicationMutation.mutateAsync({
              service,
              creditNoteId,
              ledgerTransactionId
            })
        )
        .exhaustive()
      displayNotification(
        `${upperFirst(repush.entityName)} synced to ${serviceName}`,
        { type: 'success' }
      )
    } catch (error) {
      await apiQueryClient.invalidateQueries({
        predicate: query =>
          [
            dashboardv99990101Client.getManualTasks,
            dashboardv99990101Client.getManualTask,
            dashboardv99990101Client.getAdminManualTasks
          ].some(endpoint =>
            endpoint.queryKey.every(key => query.queryKey.includes(key))
          )
      })
      displayNotification(
        `Failed to sync ${repush.entityName} to ${serviceName}`,
        { type: 'error', description: getSyncErrorDescription(error) }
      )
    }

    navigate(automationsPaths.manualTasksInbox())
  }, [
    repush,
    pushPaymentMutation,
    pushCreditNoteMutation,
    pushCreditNoteApplicationMutation,
    displayNotification,
    navigate
  ])

  return {
    repushToIntegration,
    isRepushing:
      pushPaymentMutation.isPending ||
      pushCreditNoteMutation.isPending ||
      pushCreditNoteApplicationMutation.isPending
  }
}
